/**
 * Board preferences that change how a game *plays*, kept apart from the purely
 * cosmetic settings (theme, piece style, scene).
 *
 * All three are on the Settings page and reachable from the game panel itself,
 * because the moment you want to switch them off is usually mid-game.
 */

type Key = "xq-hints" | "xq-anim" | "xq-voice";

function read(key: Key, dflt: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? dflt : v === "1";
  } catch {
    return dflt;
  }
}
function write(key: Key, on: boolean): void {
  try { localStorage.setItem(key, on ? "1" : "0"); } catch { /* storage unavailable */ }
}

/**
 * Piece hints: the legal-move dots under a selected piece, and the rings that
 * mark your pieces that are hanging or attacked. Strong players find them
 * noisy, and they are training wheels in a rated game.
 */
export const hintsOn = (): boolean => read("xq-hints", true);
export const setHints = (on: boolean): void => write("xq-hints", on);
export const toggleHints = (): boolean => { const v = !hintsOn(); setHints(v); return v; };

/** The slide-and-lift animation played when a piece moves. */
export const animOn = (): boolean => read("xq-anim", true);
export const setAnim = (on: boolean): void => write("xq-anim", on);
export const toggleAnim = (): boolean => { const v = !animOn(); setAnim(v); return v; };

/** Spoken move announcements (off by default — nobody expects a talking page). */
export const voiceOn = (): boolean => read("xq-voice", false);
export const setVoice = (on: boolean): void => write("xq-voice", on);
export const toggleVoice = (): boolean => { const v = !voiceOn(); setVoice(v); return v; };
